import { useState } from 'react';
import { Send, FileText, Plus, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { supabase } from '../../lib/supabase';
import { getOficinaId } from '../../lib/office';
import { currency } from '../../utils/formatters';

export default function QuoteForm({ osId, clienteId, veiculoId, onQuoteCreated, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([{ id: 1, item_type: 'service', description: '', quantity: 1, unit_price: 0 }]);
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState(0);

  const addItem = (type) => {
    setItems([...items, { id: Date.now(), item_type: type, description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (itemId) => {
    setItems(items.filter((item) => item.id !== itemId));
  };

  const updateItem = (itemId, field, value) => {
    setItems(items.map((item) => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const calculateSubtotal = () => {
    return items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - discount;
  };

  const handleSaveDraft = async ({ notify = true } = {}) => {
    if (items.length === 0 || items.every((item) => !item.description)) {
      toast.error('Adicione pelo menos um item ao orçamento');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const oficinaId = await getOficinaId();
      if (!oficinaId) throw new Error('Oficina não identificada');

      const { data: quote, error } = await supabase
        .from('quotes')
        .insert({
          oficina_id: oficinaId,
          os_id: osId,
          cliente_id: clienteId,
          veiculo_id: veiculoId,
          status: 'draft',
          current_version: 1,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const { data: version, error: versionError } = await supabase
        .from('quote_versions')
        .insert({
          quote_id: quote.id,
          oficina_id: oficinaId,
          version: 1,
          subtotal: calculateSubtotal(),
          discount: discount,
          total: calculateTotal(),
          notes: notes,
        })
        .select()
        .single();

      if (versionError) throw versionError;

      const quoteItems = items
        .filter((item) => item.description.trim())
        .map((item) => ({
          quote_version_id: version.id,
          oficina_id: oficinaId,
          item_type: item.item_type,
          description: item.description.trim(),
          quantity: item.quantity,
          unit_price: item.unit_price,
        }));

      const { error: itemsError } = await supabase
        .from('quote_items')
        .insert(quoteItems);

      if (itemsError) throw itemsError;

      if (notify) {
        toast.success('Orçamento salvo como rascunho');
        onQuoteCreated?.(quote);
      }
      return quote;
    } catch (error) {
      toast.error('Erro ao salvar orçamento: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSendQuote = async () => {
    const savedQuote = await handleSaveDraft({ notify: false });
    if (!savedQuote) return;
    
    setSending(true);
    try {
      const { data: token, error: tokenError } = await supabase.rpc('generate_quote_public_token', {
        target_quote_id: savedQuote.id,
      });

      if (tokenError) throw tokenError;

      const publicLink = `${window.location.origin}/orcamento/${token}`;
      
      await navigator.clipboard.writeText(publicLink);
      toast.success('Orçamento enviado! Link copiado para área de transferência');
      onQuoteCreated?.({ ...savedQuote, public_link: publicLink });
    } catch (error) {
      toast.error('Erro ao enviar orçamento: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText size={20} />
            Itens do Orçamento
          </h3>
          <p className="text-sm text-zinc-400">Adicione serviços e peças ao orçamento</p>
        </div>

        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="flex gap-2 items-start rounded-lg border border-white/10 bg-surface2 p-3">
              <select
                value={item.item_type}
                onChange={(e) => updateItem(item.id, 'item_type', e.target.value)}
                className="rounded border border-white/10 bg-surface px-2 py-1.5 text-sm"
              >
                <option value="service">Serviço</option>
                <option value="part">Peça</option>
              </select>

              <Input
                className="flex-1"
                placeholder={item.item_type === 'service' ? 'Descrição do serviço' : 'Nome da peça'}
                value={item.description}
                onChange={(e) => updateItem(item.id, 'description', e.target.value)}
              />

              <Input
                type="number"
                className="w-20"
                placeholder="Qtd"
                value={item.quantity}
                onChange={(e) => updateItem(item.id, 'quantity', Number(e.target.value))}
                min="1"
              />

              <Input
                type="number"
                className="w-28"
                placeholder="Preço"
                value={item.unit_price}
                onChange={(e) => updateItem(item.id, 'unit_price', Number(e.target.value))}
                min="0"
                step="0.01"
              />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeItem(item.id)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          <Button variant="outline" size="sm" onClick={() => addItem('service')}>
            <Plus size={14} className="mr-2" />
            Adicionar Serviço
          </Button>
          <Button variant="outline" size="sm" onClick={() => addItem('part')}>
            <Plus size={14} className="mr-2" />
            Adicionar Peça
          </Button>
        </div>
      </Card>

      <Card>
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Observações</label>
            <textarea
              className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm"
              rows={3}
              placeholder="Observações sobre o orçamento..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Desconto (R$)</label>
            <Input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(Number(e.target.value))}
              min="0"
              step="0.01"
              placeholder="0,00"
            />
          </div>

          <div className="rounded-lg border border-white/10 bg-surface2 p-4">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Subtotal:</span>
              <span>{currency(calculateSubtotal())}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-green-400">
                <span>Desconto:</span>
                <span>-{currency(discount)}</span>
              </div>
            )}
            <div className="mt-2 flex justify-between text-lg font-semibold">
              <span>Total:</span>
              <span className="text-blue-400">{currency(calculateTotal())}</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={onCancel} disabled={loading || sending}>
          Cancelar
        </Button>
        <Button onClick={handleSaveDraft} disabled={loading || sending}>
          <Save size={16} className="mr-2" />
          {loading ? 'Salvando...' : 'Salvar Rascunho'}
        </Button>
        <Button onClick={handleSendQuote} disabled={loading || sending}>
          <Send size={16} className="mr-2" />
          {sending ? 'Enviando...' : 'Enviar para Cliente'}
        </Button>
      </div>
    </div>
  );
}
